import moment from "moment";

// aiim.base
import ClosestFacility from "../../../../../../aiim/base/ClosestFacility";

// aiim.datasets
import FieldNames from "../../../../../../aiim/datasets/FieldNames";

// aiim.util
import { getAttributeValue } from "../../../../../../aiim/util/aiimUtil";

// context
import Context from "../../../../../../context/Context";

// main.Events
import { makeIcsInfo } from "../../../../Events/ics";

// main.InfoPanel
import { generateShareUrl } from "../../../../InfoPanel/shareUtil";

// main.More.Actions.BookWorkspace.WorkspaceReservation
import EsriReservationSchema from "./EsriReservationSchema";
import Office365 from "./Office365";

// util
import { stringFormatter } from "../../../../../../util/formatUtil";

const MAX_NETWORK_SORT = 10;

export function getBookingSystem() {
  const minimumReservationSchema = Context.instance.aiim.datasets.minimumReservationSchema;
  const configuration = Context.getInstance().configuration;
  const configurables = configuration.extractConfigurables();
  if(minimumReservationSchema && configurables.workspaceReservation && configurables.workspaceReservation.enabled && 
    configurables.workspaceReservation.reservationType === "esri") {
      return EsriReservationSchema.getInstance();
  }else if (minimumReservationSchema && configurables.workspaceReservation && configurables.workspaceReservation.enabled && 
    configurables.workspaceReservation.reservationType === "office365"){
      return Office365.getInstance();
  }
  return null;
}

export function getBookingSystemType() {
  const bookingSystem = getBookingSystem();
  return bookingSystem && bookingSystem.type;
}

export async function getAreaName(areaId) {
  const areas = await Context.instance.areas;
  const matchedArea = areas.find((area) => {
    const id = getAttributeValue(area.attributes, FieldNames.AREA_ID);
    return areaId === id;
  });
  const areaName = getAttributeValue(matchedArea.attributes, FieldNames.AREA_NAME);
  return areaName;
}

export function makeReservationICS(reservation, params) {
  const i18n = Context.getInstance().i18n;
  const dataset = Context.getInstance().aiim.datasets.reservations;
  const attributes = reservation.attributes;
  const geometry = reservation.geometry;
  const { item, title } = params;

  const unitName = attributes[dataset.unitNameField];
  const start = attributes[dataset.startTimeField];
  const end = attributes[dataset.endTimeField];
  const reservationTitle = attributes[dataset.titleField];
  const validFileName = (reservationTitle && reservationTitle.replace(/[/\\?%*:|"<>]/g, '-')) || "reservation";
  const filename = `${validFileName}.ics`;

  // Get title and description from reservation feature
  const name = title || i18n.more.bookWorkspace.emailSubject.replace("{workspaceName}", unitName);
  const locationUrl = generateShareUrl(item);
  const linkPattern = i18n.more.bookWorkspace.locationLinkPattern;

  // Hyperlink for email clients that support it like Outlook. 
  // Defaults to the description if the client doesn't recognize 
  // the X-ALT-DESC param on the ICS file
  const description = `${linkPattern} - ${locationUrl}`;
  const hyperlink = _makeDescriptionHyperlink(linkPattern, locationUrl);

  const reservationInfo = {
    name: name,
    description: description,
    hyperlink: hyperlink,
    location: unitName,
    date_start: start,
    date_end: end
  }

  if (params.objectId) {
    reservationInfo.objectId = params.objectId;
  }

  if (
    geometry &&
    geometry.centroid &&
    geometry.centroid.latitude &&
    geometry.centroid.longitude
  ) {
    const { latitude, longitude } = geometry.centroid;
    reservationInfo.geo = `${latitude};${longitude}`;
  }

  return { reservationInfo, filename };
}

/**
 * Makes an ICS alternate html description for certain email clients
 * @param {string} label 
 * @param {string} url 
 * @returns ICS param string
 */
function _makeDescriptionHyperlink(label, url) {
  return `X-ALT-DESC;FMTTYPE=text/html:<html><body><a href="${url}">${label}</a></body></html>`;
}

export async function downloadICSFile(reservation, params) {
  if (!reservation || !params) {
    return;
  }

  const { reservationInfo, filename } = makeReservationICS(reservation, params);
  const icsInfo = await makeIcsInfo(reservationInfo, filename);

  // Download file
  const a = document.createElement("a");
  a.style = "display: none";
  a.href = icsInfo.href;
  a.download = icsInfo.filename;
  a.click();
}

export async function sortByDistance(features, office365Results) {
  const hotelingUnits = features.map((feature) => {
    return { feature: feature };
  })
  const cf = new ClosestFacility();

  const bluedot = Context.getInstance().bluedot;
  const fromFeature = bluedot.graphic;
  const map = Context.getInstance().views.activeView.map;
  const fromSource = map.findLayerById("indoors-bluedot");
  const source = Context.getInstance().aiim.datasets.units.getSource();

  cf.euclideanSort(fromFeature, hotelingUnits, null, fromSource, source);

  const topItems = hotelingUnits.slice(0, MAX_NETWORK_SORT);
  try {
    await cf.networkSort(fromFeature, topItems, true);
  } catch(e) {
    console.error("Couldn't network sort", e);
    return _getHotelData(hotelingUnits, office365Results);
  }

  // Merge
  for (let i = 0; i < topItems.length; i++) {
    hotelingUnits[i] = topItems[i];
  }

  return _getHotelData(hotelingUnits, office365Results);
}

function _getHotelData(units, office365Results) {
  const type = getBookingSystemType();

  switch (type) {
    case "esri":
      return units.map((unit) => {
        const feature = unit.feature;
        const attributes = feature && feature.attributes;
        const unitId = getAttributeValue(attributes, FieldNames.UNIT_ID);
        return unitId;
      });
    case "office365":
      return units.map((unit) => {
        const feature = unit.feature;
        const attributes = feature && feature.attributes;
        const scheduleEmail = getAttributeValue(attributes, FieldNames.SCHEDULE_EMAIL);
        const data = office365Results.find((result) => scheduleEmail === result.scheduleId);
        return data;
      });
    default:
      return [];
  }
}

export function shouldSortHotels() {
  const bluedot = Context.getInstance().bluedot;
  return bluedot.enabled && bluedot.graphic;
}

/**
 * Scenarios in which tabs should show on the Hotel panel
 * @returns Boolean of whether to show tabs or not
 */
export function shouldShowHotelTabs() {
  return getBookingSystemType() === "esri";
}

// All day for one day (24 hours)
function _isAllDayOne(startTime, endTime) {
  const incrementDay = moment(startTime).add(1, 'd');
  return endTime.isSame(incrementDay, 'day');
}

// All day for multiple days
function _isAllDayMany(startTime, endTime) {
  const incrementDay = moment(startTime).add(1, 'd');
  return endTime.isAfter(incrementDay, 'day');
}

/**
 * Format a readable date string based on the provided start and end moment objects,
 * and whether the booking is all day or not
 *
 * @param {moment} startTime
 * @param {moment} endTime
 * @param {boolean} isAllDay
 * @returns Formatted date string
 */
export function formatDate(startTime, endTime, isAllDay) {
  const i18n = Context.getInstance().i18n;

  if (isAllDay && _isAllDayOne(startTime, endTime)) {
    // Ex: May 22, all day
    const pairs = {
      date: startTime.format("MMM D")
    };
    return stringFormatter(i18n.more.bookWorkspace.bookingSameDayAllDay, pairs);
  } else if (isAllDay && _isAllDayMany(startTime, endTime)) {
    // Ex: May 22 - May 24, all day
    // Need to subtract one minute from end time to show an accurate time period
    // Ex: May 22 12:00am - May 24 12:00am NOT EQUAL TO May 22 - May 24, all day
    // Must be May 22 - May 23, all day (the booking is not "all day" on the 24th as well).
    const pairs = {
      startDate: startTime.format("MMM D"),
      endDate: moment(endTime).subtract(1, 'm').format("MMM D")
    };
    return stringFormatter(i18n.more.bookWorkspace.bookingDifferentDaysAllDay, pairs);
  } else if (startTime.isSame(endTime, 'day')) {
    // Ex: May 22, 10:00am - 4:45pm
    const pairs = {
      date: startTime.format("MMM D"),
      startTime: startTime.format("h:mma"),
      endTime: endTime.format("h:mma")
    };
    return stringFormatter(i18n.more.bookWorkspace.bookingSameDay, pairs);
  } else {
    // Ex: May 22, 2:00pm - May 23, 3:30pm
    const pairs = {
      startDate: startTime.format("MMM D"),
      endDate: endTime.format("MMM D"),
      startTime: startTime.format("h:mma"),
      endTime: endTime.format("h:mma")
    };
    return stringFormatter(i18n.more.bookWorkspace.bookingDifferentDays, pairs);
  }
}

export function makeStartDateTime(startDate, startTime, allDay) {
  const lib = Context.getInstance().lib;
  const date = lib.dojo.locale.format(startDate, {
    selector: "date",
    datePattern: "yyyy/MM/dd"
  });

  let hour = startTime.hour();
  if (hour < 10) {
    hour = `0${hour}`;
  }
  let minute = startTime.minute();
  if (minute < 10) {
    minute = `0${minute}`;
  }
  let time = `${hour}:${minute}:00`;

  if (allDay) {
    time = "00:00:00"
  }

  const checkInDate = `${date} ${time}`;
  const finalDate = new Date(checkInDate).toISOString();

  return finalDate;
}

export function makeEndDateTime(endDate, endTime, allDay) {
  const lib = Context.getInstance().lib;
  let date = lib.dojo.locale.format(endDate, {
    selector: "date",
    datePattern: "yyyy/MM/dd"
  });

  let hour = endTime.hour();
  if (hour < 10) {
    hour = `0${hour}`;
  }
  let minute = endTime.minute();
  if (minute < 10) {
    minute = `0${minute}`;
  }
  let time = `${hour}:${minute}:00`;

  if (allDay) {
    let nextDay = new Date(endDate);
    nextDay.setDate(endDate.getDate() + 1);
    date = lib.dojo.locale.format(nextDay, {
      selector: "date",
      datePattern: "yyyy/MM/dd"
    });
    time = "00:00:00";
  }

  const checkOutDate = `${date} ${time}`;
  const finalDate = new Date(checkOutDate).toISOString();

  return finalDate;
}

/**
 * Make the initial filter times for hoteling (nearest half-hour start time, then an hour
 * later end time)
 */
export function makeInitialTimes() {
  const locale = Context.instance.lib.dojo.kernel.locale;
  const format = (locale === "en" || locale === "en-us") ? "h:mm A" : "H:mm";

  const now = moment();
  const remainder = 30 - (now.minute() % 30);
  const startTime = moment(now, format).add(remainder, "minutes");
  const endTime = moment(startTime, format).add(1, "hour");

  return { startTime, endTime };
}
